/** Strip wrapping quotes some deploy platforms add to env values. */
function readEnv(value: string | undefined): string | undefined {
  if (!value) return undefined;

  const trimmed = value.trim();
  if (
    (trimmed.startsWith('"') && trimmed.endsWith('"')) ||
    (trimmed.startsWith("'") && trimmed.endsWith("'"))
  ) {
    return trimmed.slice(1, -1);
  }

  return trimmed;
}

function readFirstEnv(...values: (string | undefined)[]): string | undefined {
  for (const value of values) {
    const parsed = readEnv(value);
    if (parsed) return parsed;
  }
  return undefined;
}

/**
 * `@xinchuantw/auth-react`'s `initializeAuthSDK` only switches to its
 * production URL preset (auth-api.xinchuan.tw / auth-v2.xinchuan.tw) when
 * `env` is the *exact* literal string `"production"` — anything else
 * (missing, "local", "prod", "Production", trailing whitespace, ...)
 * silently falls back to staging (auth-api.staging.../auth-v2-staging...).
 * That mismatch is invisible in the UI until you hit a 401 on an API that
 * validates the session against prod, or logout bounces to the staging
 * login page — so normalize aggressively and warn instead of failing silent.
 */
function normalizeEnvironment(value: string | undefined): 'production' | 'staging' {
  const normalized = value?.trim().toLowerCase();

  if (normalized === 'production' || normalized === 'prod') return 'production';
  if (!normalized || normalized === 'local' || normalized === 'staging') return 'staging';

  console.warn(
    `[env] Unrecognized VITE_ENV/VITE_ENVIRONMENT value "${value}" — falling back to "staging". ` +
      'Set it to exactly "production" for production deploys.'
  );
  return 'staging';
}

export const config = {
  // Called directly (no same-origin proxy/BFF) — the API sets CORS headers for
  // the frontend origins and accepts the XC-ACC-TOKEN-* session cookie directly.
  apiBaseUrl:
    readEnv(import.meta.env.VITE_API_BASE_URL) ?? 'https://vouc-v2-staging.xinchuan.tw/',
  apiFulfillmentBaseUrl:
    readEnv(import.meta.env.VITE_API_FULFILLMENT_BASE_URL) ?? 'https://fulfillment-api.staging.xinchuan.tw/',
  // Called directly. The kiosk API allows credentialed CORS for the frontend origins.
  apiKioskBaseUrl:
    readEnv(import.meta.env.VITE_API_KIOSK_BASE_URL) ??
    'https://hokishop-sim-kiosk-api.staging.xinchuan.tw/',
  apiVoucherPinBaseUrl:
    readEnv(import.meta.env.VITE_API_VOUCHER_PIN_BASE_URL) ??
    'https://voucherpin-api.staging.xinchuan.tw/',
  /** `staging` | `production` — used by `@xinchuantw/auth-react` */
  environment: normalizeEnvironment(
    readFirstEnv(import.meta.env.VITE_ENV, import.meta.env.VITE_ENVIRONMENT)
  ),
  assetUrl: readEnv(import.meta.env.VITE_ASSET_URL),
  utilitiesUrl:
    readFirstEnv(import.meta.env.VITE_UTILITIES_API_BASE_URL, import.meta.env.VITE_UTILITIES_URL) ??
    'https://utilitiesapi.staging.xinchuan.tw/',
  // Unlike fulfillment-api/auth-api, the data lake has no CORS config at all,
  // so it can't be called directly — it's proxied same-origin at `/datalake`
  // (vite.config.ts locally, netlify/edge-functions/datalake-proxy.ts when
  // deployed), which injects the Bearer token server-side so it never reaches
  // the client bundle.
  apiDataLakeUrl: '/datalake',
};
